/* filepath: /Users/george/MatrizInc/MarketBrewer/Clients/street-lawyer-services/website/src/core/Pages/ProductsPage/ProductCategories/gsap.ts */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

type CategoryRefsType = {
  current: (HTMLElement | null)[];
};

/**
 * Animates the section title with a fade-in and slide-up effect
 */
export const animateTitle = (
  titleRef: RefType,
  sectionRef: RefType
): ScrollTrigger | undefined => {
  if (!titleRef.current || !sectionRef.current) return;

  const titleTrigger = ScrollTrigger.create({
    id: "product-categories-title",
    trigger: titleRef.current,
    start: "top 80%",
    once: true,
  });

  gsap.fromTo(
    titleRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      scrollTrigger: titleTrigger,
    }
  );

  return titleTrigger;
};

/**
 * Animates category cards with a staggered entrance effect
 * Takes imagesLoaded state to ensure animations happen after images are loaded
 */
export const animateCategories = (
  categoryRefs: CategoryRefsType,
  sectionRef: RefType,
  imagesLoaded: Record<string, boolean> = {}
): ScrollTrigger[] => {
  if (!sectionRef.current) return [];

  const categories = categoryRefs.current.filter(Boolean) as HTMLElement[];
  const createdTriggers: ScrollTrigger[] = [];

  categories.forEach((category, index) => {
    // Get category id from data attribute or use index as fallback
    const categoryId = category.dataset.categoryId || `category-${index}`;

    const categoryTrigger = ScrollTrigger.create({
      id: `product-category-${index}`,
      trigger: category,
      start: "top 85%",
      once: true,
    });

    // Enhanced animation that respects image loading state
    gsap.fromTo(
      category,
      {
        opacity: 0,
        y: 50,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        // Slightly longer delay for cards with loaded images
        delay: index * 0.15 + (imagesLoaded[categoryId] ? 0 : 0.1),
        scrollTrigger: categoryTrigger,
        onStart: () => {
          console.log(`Starting animation for category ${index}`);
        },
      }
    );

    createdTriggers.push(categoryTrigger);
  });

  return createdTriggers;
};

/**
 * Cleans up ScrollTrigger instances specific to this component
 */
export const cleanupScrollTriggers = (): void => {
  ScrollTrigger.getAll()
    .filter(
      (trigger) =>
        trigger.vars.id?.startsWith("product-categories-") ||
        trigger.vars.id?.startsWith("product-category-")
    )
    .forEach((trigger) => trigger.kill());
};

/**
 * Fade in an image element
 */
export const fadeInImage = (
  imageElement: HTMLImageElement,
  delay: number = 0
): void => {
  gsap.to(imageElement, {
    opacity: 1,
    duration: 0.5,
    delay,
    ease: "power2.out",
  });
};
